import React, { useCallback, useMemo, useState } from 'react';
import styled from '@emotion/styled';
import { useTheme, getUniqueId } from 'basis';

const Field =
  import.meta.env.MODE === 'test'
    ? require('basis/dist/components/internal/Field').default
    : require('basis/dist/esm/components/internal/Field').default;
const useField =
  import.meta.env.MODE === 'test'
    ? require('basis/dist/hooks/internal/useField').default
    : require('basis/dist/esm/hooks/internal/useField').default;

const StyledSelect = styled.select(({ theme, value, isValid }) =>
  theme.select.getCSS({
    color: 'white',
    border: `1px solid ${theme.colors.neutrals.t400}`,
    fullWidth: true,
    value,
    isValid,
  }),
);

export function GroupedSelect({
  name,
  label,
  placeholder = 'Please select',
  groups,
  helpText,
  optional = false,
  disabled = false,
  testId,
}) {
  const theme = useTheme();
  const [id] = useState(() => `grouped-select-${getUniqueId()}`);
  const [auxId] = useState(() => `grouped-select-aux-${getUniqueId()}`);

  const isEmpty = useCallback(value => !groups.some(g => g.options.some(o => o.value === value)), [groups]);
  const data = useMemo(() => ({ isEmpty }), [isEmpty]);
  const validate = useCallback((v, h) => (h.isEmpty(v) ? 'Please make a selection.' : null), []);

  const { value = '', errors, hasErrors, onFocus, onBlur, onChange } = useField('GroupedSelect', {
    name,
    disabled,
    optional,
    validate,
    data,
  });

  return (
    <Field
      fullWidth
      optional={optional}
      disabled={disabled}
      label={label}
      labelFor={id}
      auxId={auxId}
      helpText={helpText}
      errors={errors}
      testId={testId}
    >
      <StyledSelect
        theme={theme}
        id={id}
        name={name}
        value={value}
        disabled={disabled}
        onFocus={onFocus}
        onBlur={onBlur}
        onChange={onChange}
        aria-invalid={hasErrors ? 'true' : null}
        aria-describedby={helpText || hasErrors ? auxId : null}
        isValid={!hasErrors}
      >
        <option value="" disabled={!optional} hidden>
          {placeholder}
        </option>
        {groups.map(group => (
          <optgroup key={group.label} label={group.label}>
            {group.options.map(o => (
              <option key={o.label} value={o.value}>
                {o.label}
              </option>
            ))}
          </optgroup>
        ))}
      </StyledSelect>
    </Field>
  );
}
